/**
 * Device-local experience-mode override.
 *
 * Better Auth cookie-caches get-session for 5 minutes. PUT /experience-mode
 * writes the DB via raw SQL, so a following session refresh can still return
 * Full. Theme survives that because it also paints localStorage; Lite did
 * not, so the dashboard kept showing the Full "Start meeting" dialog.
 *
 * This store is the Lite equivalent of `lex:theme`: write it on toggle so
 * chrome (nav, Start recording, instant start) flips immediately, and read
 * it ahead of a stale session value. GET /api/users/me/experience-mode
 * hydrates it from the DB on auth layout mount (that route is not cookie
 * cached).
 */

import { DEFAULT_EXPERIENCE_MODE, isExperienceMode, type TExperienceMode } from "@lexicanon/shared"

export const EXPERIENCE_MODE_STORAGE_KEY = "lex:experience-mode"

const listeners = new Set<() => void>()

function readStorage(): TExperienceMode | null {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(EXPERIENCE_MODE_STORAGE_KEY)
    return isExperienceMode(raw) ? raw : null
  } catch {
    return null
  }
}

let memory: TExperienceMode | null = readStorage()

function emit(): void {
  for (const listener of listeners) listener()
}

export function getExperienceModeSnapshot(): TExperienceMode | null {
  return memory
}

export function subscribeExperienceMode(listener: () => void): () => void {
  listeners.add(listener)
  return (): void => {
    listeners.delete(listener)
  }
}

export function setExperienceModeLocal(mode: TExperienceMode): void {
  memory = mode
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(EXPERIENCE_MODE_STORAGE_KEY, mode)
    } catch {
      // private mode — in-memory still drives this tab
    }
  }
  emit()
}

export function clearExperienceModeLocal(): void {
  memory = null
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(EXPERIENCE_MODE_STORAGE_KEY)
    } catch {
      // ignore
    }
  }
  emit()
}

/** Test-only: drop in-memory + storage so suites don't leak across files. */
export function resetExperienceModeStoreForTests(): void {
  memory = null
  listeners.clear()
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(EXPERIENCE_MODE_STORAGE_KEY)
    } catch {
      // ignore
    }
  }
}

export function resolveExperienceMode(sessionValue: unknown): TExperienceMode {
  if (memory && isExperienceMode(memory)) return memory
  return isExperienceMode(sessionValue) ? sessionValue : DEFAULT_EXPERIENCE_MODE
}
